import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { logger } from "../../lib/logger";

const PLUGINS = [
  {
    key: "enable_auctions",
    label: "Auction Service",
    desc: "Enable the live auction module (/auctions, bidding, WebSocket).",
  },
  {
    key: "enable_uploads",
    label: "Image Uploads",
    desc: "Allow artists to upload artwork images directly to the platform.",
  },
  {
    key: "enable_stripe_checkout",
    label: "Stripe Checkout",
    desc: "Enable buyers to purchase artworks via Stripe payment.",
  },
  {
    key: "enable_email_notifications",
    label: "Email Notifications",
    desc: "Send transactional emails (outbid, won, sold). Requires RESEND_API_KEY in .env.",
  },
  {
    key: "enable_registration",
    label: "Open Registration",
    desc: "Allow new sign-ups. Disable to lock the platform to existing users.",
  },
  {
    key: "maintenance_mode",
    label: "Maintenance Mode",
    desc: "Show a maintenance banner site-wide. Use during deploys.",
  },
];

const NUMERIC_FIELDS = [
  { key: "upload_quota_per_day", label: "Upload quota / user / day", min: 1, max: 1000 },
  { key: "max_file_size_mb", label: "Max file size (MB)", min: 1, max: 50 },
  { key: "platform_commission_pct", label: "Platform commission %", min: 0, max: 50 },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then(({ data }) => setSettings(data))
      .catch((err) => logger.error("Settings load failed:", err));
  }, []);

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = {
        enable_auctions: settings.enable_auctions,
        enable_uploads: settings.enable_uploads,
        enable_stripe_checkout: settings.enable_stripe_checkout,
        enable_email_notifications: settings.enable_email_notifications,
        enable_registration: settings.enable_registration,
        maintenance_mode: settings.maintenance_mode,
        upload_quota_per_day: parseInt(settings.upload_quota_per_day) || 30,
        max_file_size_mb: parseInt(settings.max_file_size_mb) || 10,
        platform_commission_pct: parseFloat(settings.platform_commission_pct) || 0,
        site_announcement: settings.site_announcement || "",
      };
      const { data } = await api.patch("/admin/settings", payload);
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="label-eyebrow text-[#555]">Loading…</p>;

  return (
    <div data-testid="admin-settings">
      <p className="label-eyebrow text-[#1E3A8A]">— Configuration</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Plugins & Settings
      </h1>
      <p className="text-[#555] mt-3 max-w-2xl">
        Toggle features on or off across the platform. Changes apply immediately.
      </p>

      <div className="mt-12">
        <h2 className="font-serif-display text-2xl text-[#111] mb-6">Feature plugins</h2>
        <div className="border border-[#E5E0D8] bg-white divide-y divide-[#E5E0D8]">
          {PLUGINS.map((p) => (
            <div
              key={p.key}
              data-testid={`plugin-${p.key}`}
              className="flex items-start justify-between gap-6 p-6"
            >
              <div className="flex-1">
                <p className="font-serif-display text-xl text-[#111]">{p.label}</p>
                <p className="text-sm text-[#555] mt-1">{p.desc}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`label-eyebrow ${settings[p.key] ? "text-[#1E3A8A]" : "text-[#999]"}`}>
                  {settings[p.key] ? "Enabled" : "Disabled"}
                </span>
                <Switch
                  data-testid={`toggle-${p.key}`}
                  checked={!!settings[p.key]}
                  onCheckedChange={(v) => update(p.key, v)}
                  className="data-[state=checked]:bg-[#1E3A8A]"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-serif-display text-2xl text-[#111] mb-6">Limits & policy</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {NUMERIC_FIELDS.map((f) => (
            <div key={f.key} className="border border-[#E5E0D8] bg-white p-5">
              <label className="label-eyebrow text-[#555]">{f.label}</label>
              <Input
                data-testid={`field-${f.key}`}
                type="number"
                min={f.min}
                max={f.max}
                value={settings[f.key] ?? 0}
                onChange={(e) => update(f.key, e.target.value)}
                className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 font-mono-aam text-2xl focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-serif-display text-2xl text-[#111] mb-6">Site-wide announcement</h2>
        <div className="border border-[#E5E0D8] bg-white p-5">
          <label className="label-eyebrow text-[#555]">Banner text (leave empty to hide)</label>
          <Textarea
            data-testid="field-site_announcement"
            rows={2}
            value={settings.site_announcement || ""}
            onChange={(e) => update("site_announcement", e.target.value)}
            placeholder="e.g. ✨ Spring Auction: new lots live now."
            className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#1E3A8A]"
          />
        </div>
      </div>

      <div className="mt-12 flex items-center gap-4">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-settings-btn"
          className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-8 py-5 h-auto label-eyebrow"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <span className="label-eyebrow text-[#555]">
          Last updated · {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : "—"}
        </span>
      </div>
    </div>
  );
}
